import { Component, DestroyRef, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { API, CompanyDTO, ProductFullDTO } from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { UtilService } from '../../../services/util.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CompaniesComponent } from '../../company/companies/companies.component';
import { NavigationService } from '@ngx-seatback/utils';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-products-edit',
  templateUrl: './products-edit.component.html',
  styleUrls: ['./products-edit.component.scss'],
  standalone: false,
})
export class ProductsEditComponent implements OnInit {
  product: ProductFullDTO;
  productId: number;
  loading: boolean;
  form: UntypedFormGroup;

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    public errorHandler: ErrorService,
    public modal: ModalService,
    public util: UtilService,
    private navService: NavigationService,
    private destroyRef: DestroyRef
  ) {
    this.productId = null;
    this.loading = true;
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', [Validators.required]),
      PLU: new UntypedFormControl('', []),
      GTIN: new UntypedFormControl('', []),
      ASIN: new UntypedFormControl('', []),
      Description: new UntypedFormControl('', []),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  get PLU() {
    return this.form.get('PLU');
  }

  get ASIN() {
    return this.form.get('ASIN');
  }

  get GTIN() {
    return this.form.get('GTIN');
  }

  get Description() {
    return this.form.get('Description');
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.productId = params.productID;
      this.refreshFromServer();
    });
    this.registerFormChangesListener();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.product.name = value;
    });
    this.PLU.valueChanges.subscribe(value => {
      this.product.plu = value;
    });
    this.GTIN.valueChanges.subscribe(value => {
      this.product.gtin = value;
    });
    this.ASIN.valueChanges.subscribe(value => {
      this.product.asin = value;
    });
    this.Description.valueChanges.subscribe(value => {
      this.product.description = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.product.name);
    this.Name.markAsTouched();
    this.PLU.setValue(this.product.plu);
    this.PLU.markAsTouched();
    this.ASIN.setValue(this.product.asin);
    this.ASIN.markAsTouched();
    this.GTIN.setValue(this.product.gtin);
    this.GTIN.markAsTouched();
    this.Description.setValue(this.product.description);
    this.Description.markAsTouched();
  }

  nextStep(wizard: SbWizardComponent, nextTab: SbWizardTabComponent) {
    wizard.selectTab(nextTab);
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.productId) {
      this.product = new ProductFullDTO();
      this.loading = false;
    } else {
      this.api
        .pm_Product_retrieveById(this.productId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (data: ProductFullDTO) => {
            this.product = data;
            this.updateForm();
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updateProductCall() {
    this.loading = true;
    if (!this.product.id) {
      if (this.product.name) {
        this.api
          .pm_Product_createProduct(this.product)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (createdProduct: ProductFullDTO) => {
              this.product = createdProduct;
              this.navService.navigateToPoint(this.util.RoutingID.productDetails, [{ id: 'productID', value: createdProduct?.id }]);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    } else {
      this.api
        .pm_Product_editProduct(this.product)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (editedProduct: ProductFullDTO) => {
            this.product = editedProduct;
            this.navService.navigateToPoint(this.util.RoutingID.productDetails, [{ id: 'productID', value: editedProduct?.id }]);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  selectCompany() {
    this.modal
      .showNavigationModal({
        entryComponent: { value: CompaniesComponent, data: {} },
        title: null,
        modalOptions: { windowClass: 'bigXL' },
      })
      .then((result: CompanyDTO) => {
        if (result) {
          this.product.manufacturer = result;
        } else {
          this.product.manufacturer = null;
        }
      })
      .catch(() => {});
  }
}
